import React from "react";
import GlassTrack from "./GlassTrack";
import usePlayer, { formatPlayback, player } from "../hooks/usePlayer";
import { ReactComponent as Next } from "../assets/svgs/sf-symbols/control-center/forward.fill.svg";
import { ReactComponent as Prev } from "../assets/svgs/sf-symbols/control-center/backward.fill.svg";
import { ReactComponent as Play } from "../assets/svgs/sf-symbols/control-center/play.fill.svg";
import { ReactComponent as Pause } from "../assets/svgs/sf-symbols/control-center/pause.fill.svg";

// Artwork that settles slightly smaller while paused, like Apple Music.
const Artwork = ({ track, playing }) => (
  <img src={track.image} alt="" className={`np-art np-art--tile ${playing ? "is-playing" : ""}`} />
);

// The 2x2 Control Center module. Clicking the artwork or title opens the full list.
export const NowPlayingTile = ({ onExpand }) => {
  const { track, playing } = usePlayer();
  return (
    <div className="np-tile">
      <button type="button" className="np-expand" onClick={onExpand} aria-label="Show Now Playing">
        <Artwork track={track} playing={playing} />
        <span className="np-meta">
          <span className="cc-title np-line">{track.title}</span>
          <span className="cc-subtitle np-line">{track.artist}</span>
        </span>
      </button>
      <div className="np-controls">
        <button type="button" onClick={player.previous} className="np-control" aria-label="Previous track">
          <Prev className="np-skip" />
        </button>
        <button type="button" onClick={player.toggle} className="np-control" aria-label={playing ? "Pause" : "Play"}>
          {playing ? <Pause className="np-play" /> : <Play className="np-play" />}
        </button>
        <button type="button" onClick={player.next} className="np-control" aria-label="Next track">
          <Next className="np-skip" />
        </button>
      </div>
    </div>
  );
};

// Apple's audio indicator: flat dots while paused, bouncing bars while playing.
const Waveform = ({ playing }) => (
  <span className={`np-wave ${playing ? "is-playing" : ""}`} aria-hidden="true">
    <i />
    <i />
    <i />
    <i />
    <i />
  </span>
);

// Expanded Now Playing for a single source, as in macOS Tahoe: large artwork, then the title,
// controls, and a blue progress bar you can scrub, with elapsed and remaining time.
export const NowPlayingExpanded = () => {
  const { track, playing, time, duration } = usePlayer();
  return (
    <div className="np-expanded" aria-label="Now Playing">
      <img src={track.image} alt="" className="np-expanded-art" />
      <div className="np-expanded-body">
        <div className="np-expanded-head">
          <span className="np-meta flex-1">
            <span className="np-source-title np-line">{track.title}</span>
            <span className="np-source-artist np-line">{track.artist}</span>
          </span>
          <Waveform playing={playing} />
        </div>
        <div className="np-expanded-controls">
          <button type="button" onClick={player.previous} className="np-source-control" aria-label="Previous track">
            <Prev className="np-skip" />
          </button>
          <button
            type="button"
            onClick={player.toggle}
            className="np-source-control"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause className="np-play" /> : <Play className="np-play" />}
          </button>
          <button type="button" onClick={player.next} className="np-source-control" aria-label="Next track">
            <Next className="np-skip" />
          </button>
        </div>
        <GlassTrack
          accent
          value={Math.min(time, duration || 0)}
          max={duration || 1}
          step="any"
          onChange={player.seek}
          label="Playback position"
          valueText={`${formatPlayback(time)} of ${formatPlayback(duration)}`}
        />
        <div className="np-times">
          <span>{formatPlayback(time)}</span>
          <span>−{duration ? formatPlayback(duration - time) : "--:--"}</span>
        </div>
      </div>
    </div>
  );
};
